import { motion } from "framer-motion";
import { Play, Pause, Loader2, Radio } from "lucide-react";
import radioConfig from "../config/radioConfig";
import { useAudioPlayer } from "../context/AudioPlayerContext";
import { useCurrentShow } from "../hooks/useCurrentShow";

// Barras del visualizador (estéticas). Cuando el audio está sonando se animan.
const BARS = [0.4, 0.75, 0.5, 0.95, 0.65, 0.85, 0.45, 0.7, 0.55, 0.9, 0.5, 0.8];

export const LivePlayer = () => {
  const { isPlaying, isLoading, hasError, toggle } = useAudioPlayer();
  const active = isPlaying || isLoading;
  const currentShow = useCurrentShow(radioConfig.schedule);
  const onAirText = currentShow ? currentShow.name : "La mejor música sonando ahora";

  return (
    <section id="en-vivo" className="relative py-20 sm:py-28 bg-[#070A0F]">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono text-cyan-400 mb-3">
            Reproductor
          </p>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold text-white mb-10">
            Ahora en vivo
          </h2>
          <p className="-mt-7 mb-10 text-sm font-mono text-slate-400">
            Al aire ahora: <span className="text-cyan-300">{onAirText}</span>
          </p>
        </motion.div>

        <motion.div
          data-testid="live-player-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="relative rounded-3xl border border-cyan-500/30 bg-[#0D131E] p-6 sm:p-10 overflow-hidden shadow-[0_0_50px_rgba(0,229,255,0.08)]"
        >
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-cyan-500/10 blur-[100px]" />

          <div className="relative flex flex-col md:flex-row md:items-center gap-8">
            {/* Play + logo */}
            <div className="flex items-center gap-5">
              <button
                onClick={toggle}
                data-testid="live-player-play-button"
                aria-label={isPlaying ? "Pausar transmisión" : "Escuchar en vivo"}
                className="group relative flex-shrink-0 h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-cyan-400 hover:bg-cyan-300 text-[#070A0F] flex items-center justify-center transition-[background-color,transform] duration-200 hover:scale-105 shadow-[0_0_40px_rgba(0,229,255,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {active && (
                  <span className="absolute inset-0 rounded-full border border-cyan-300/40 animate-ping-slow" aria-hidden="true" />
                )}
                {isLoading ? (
                  <Loader2 size={34} className="animate-spin" />
                ) : isPlaying ? (
                  <Pause size={34} fill="currentColor" />
                ) : (
                  <Play size={34} fill="currentColor" className="ml-1" />
                )}
              </button>

              <div>
                <div
                  data-testid="live-player-status-badge"
                  className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-2"
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400/70" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                  </span>
                  <span className="text-[11px] font-mono uppercase tracking-widest text-emerald-300">
                    En vivo
                  </span>
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-none">
                  FM REAL <span className="text-cyan-400">96.3</span>
                </h3>
                <p className="text-sm text-slate-400 mt-1 font-mono">
                  {radioConfig.city}, {radioConfig.province}
                </p>
              </div>
            </div>

            {/* Visualizer */}
            <div className="flex-1 flex items-end justify-center md:justify-end gap-1.5 h-16 eq-group cursor-pointer" title="Transmisión en vivo 96.3 MHz">
              {BARS.map((h, i) => (
                <span
                  key={i}
                  className="w-1.5 sm:w-2 rounded-full bg-gradient-to-t from-cyan-500/40 to-cyan-300 eq-bar"
                  style={{
                    height: `${h * 100}%`,
                    animationDelay: `${i * 0.08}s`,
                  }}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>

          {/* Specs + estado */}
          <div className="relative mt-8 pt-6 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <p className="text-xs font-mono text-slate-500 tracking-wide">
              96.3 MHz · ESTUDIO: LAS BREÑAS · STREAMING HD
            </p>
            <span
              data-testid="live-player-state-text"
              className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400"
            >
              <Radio size={15} className={isPlaying ? "animate-pulse" : ""} />
              {isLoading
                ? "Conectando con la transmisión…"
                : isPlaying
                ? "Reproduciendo en vivo"
                : hasError
                ? "No se pudo conectar. Tocá play para volver a intentar."
                : "Tocá play para escuchar en vivo"}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LivePlayer;
